import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, ExternalLink, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * DownloadLinkDialog
 * ------------------
 * Öffnet die übergebene Download-URL primär via `window.open(..., '_blank')`
 * (TOP-LEVEL Tab — umgeht den Iframe-Sandbox-Block des Emergent-Previews).
 *
 * Zeigt die URL zusätzlich in einem Input-Feld mit Kopier- und „Erneut
 * öffnen"-Button an — als Fallback, falls der Pop-up-Blocker zugeschlagen
 * hat oder die Sandbox `window.open` blockt.
 *
 * Props:
 *   open        – ob der Dialog offen ist
 *   onOpenChange– setter für open
 *   url         – die fertige Download-URL (inkl. Token)
 *   title       – Dialog-Titel (z. B. „Backup herunterladen")
 *   description – Hinweis-Text
 *   filename    – Datei-Name (rein zur Info)
 *   expiresInSec – TTL in Sekunden (rein zur Info)
 */
export default function DownloadLinkDialog({
    open,
    onOpenChange,
    url,
    title = "Download",
    description = 'Die URL ist bereits markiert — kopieren Sie sie mit dem „Kopieren"-Button oder Strg+C und fügen Sie sie in eine neue Browser-Tab-Adressleiste ein. Der Download startet dann automatisch.',
    filename = "",
    expiresInSec = 600,
}) {
    const [copied, setCopied] = useState(false);
    const inputRef = useRef(null);

    // Beim Öffnen des Dialogs die URL automatisch markieren,
    // damit der Benutzer sofort Strg/Cmd+C drücken kann.
    // KEIN automatisches window.open mehr — das verursacht ein
    // störendes about:blank-Pop-up-Fenster im Preview-Iframe.
    useEffect(() => {
        if (!open) {
            setCopied(false);
            return;
        }
        // Nach Render: Input fokussieren und Inhalt selektieren
        const t = setTimeout(() => {
            try {
                inputRef.current?.focus();
                inputRef.current?.select();
            } catch { /* ignore */ }
        }, 80);
        return () => clearTimeout(t);
    }, [open, url]);

    const handleCopy = async () => {
        // Strategie:
        // 1) Modernes Clipboard-API (klappt nur, wenn der Iframe die
        //    Permission ‚clipboard-write' bekommt — im Emergent-Preview
        //    aktuell NICHT der Fall).
        // 2) Fallback: Input-Feld selektieren + document.execCommand("copy")
        //    (funktioniert in den meisten Browsern auch in sandboxed iframes,
        //    weil es vom Benutzer-Klick getriggert wird).
        // 3) Letzter Fallback: nur selektieren + Toast „Strg+C drücken".
        let success = false;
        // Versuch 1
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                success = true;
            }
        } catch { /* fällt durch zu Versuch 2 */ }
        // Versuch 2 (execCommand) — nur wenn 1 fehlgeschlagen
        if (!success) {
            try {
                const el = inputRef.current;
                if (el) {
                    el.focus();
                    el.select();
                    el.setSelectionRange(0, url.length);
                    success = document.execCommand("copy");
                }
            } catch { /* fällt durch zu 3 */ }
        }
        if (success) {
            setCopied(true);
            toast.success("URL in Zwischenablage kopiert");
            setTimeout(() => setCopied(false), 2500);
        } else {
            // URL ist bereits markiert — Benutzer kann Strg/Cmd+C drücken
            try {
                inputRef.current?.focus();
                inputRef.current?.select();
            } catch { /* ignore */ }
            toast.info("URL ist markiert – bitte Strg+C (Cmd+C) drücken zum Kopieren", { duration: 5000 });
        }
    };

    const handleOpen = () => {
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (!w) {
            toast.info("Pop-up wurde blockiert – bitte URL kopieren und im Browser-Tab öffnen");
        }
    };

    const minutes = Math.round(expiresInSec / 60);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="download-link-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {filename && (
                        <div className="text-xs text-muted-foreground">
                            Datei: <span className="text-foreground font-mono">{filename}</span>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-widest text-muted-foreground">
                            Download-URL (gültig ca. {minutes} Min.)
                        </label>
                        <div className="flex gap-2">
                            <Input
                                ref={inputRef}
                                value={url || ""}
                                readOnly
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => e.target.select()}
                                className="font-mono text-xs"
                                data-testid="download-link-url"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCopy}
                                data-testid="download-link-copy"
                                className={copied ? "border-emerald-500/50 text-emerald-400" : ""}
                            >
                                {copied ? (
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                ) : (
                                    <Copy className="h-4 w-4 mr-2" />
                                )}
                                {copied ? "Kopiert" : "Kopieren"}
                            </Button>
                        </div>
                    </div>

                    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                        <Info className="h-4 w-4 shrink-0 mt-0.5" />
                        <div>
                            <strong>So funktioniert es:</strong> Klicken Sie auf „Kopieren"
                            (oder drücken Sie Strg+C, die URL ist bereits markiert), öffnen
                            Sie einen neuen Browser-Tab und fügen Sie die URL dort ein
                            — der Download startet automatisch.
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex-row gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="download-link-close"
                    >
                        Schließen
                    </Button>
                    <Button
                        type="button"
                        onClick={handleOpen}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="download-link-open"
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Im neuen Tab öffnen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
